import { ServiceEntityType } from '@consigli/types';

import { ServiceFormData } from '../types';
import { ServiceFormState } from './use-unified-service-form';
import { BuildingPackageForm } from '../building-package-form';
import { DocumentPackageForm } from '../document-package-form';
import { LayoutOptimizationForm } from '../layout-optimization-form';

interface ServiceFormRendererProps {
  serviceType: ServiceEntityType | '';
  projectId: number;
  state: ServiceFormState;
  errors: Partial<Record<keyof ServiceFormData, string>>;
  onFieldChange: <K extends keyof ServiceFormState>(field: K, value: ServiceFormState[K]) => void;
}

/**
 * This component renders the appropriate form based on the service type.
 * This allows us to have a single form component that can be used for all service types.
 * @param serviceType - The type of service to render the form for.
 * @param state - The state of the service form.
 * @param errors - The errors of the service form.
 * @param onFieldChange - The function to call when a field is changed.
 * @returns The appropriate form based on the service type.
 */
export const ServiceFormRenderer = ({
  serviceType,
  projectId,
  state,
  errors,
  onFieldChange,
}: ServiceFormRendererProps) => {
  // Convert errors to the format expected by form components (filter out undefined values)
  const formErrors: Record<string, string> = Object.fromEntries(
    Object.entries(errors).filter(([_, value]) => value !== undefined) as [string, string][],
  );

  if (serviceType === ServiceEntityType.DOCUMENT_PACKAGE) {
    return (
      <DocumentPackageForm
        projectId={projectId}
        name={state.name}
        packageType={state.documentPackageType}
        language={state.language}
        files={state.files}
        selectedConflictTypes={state.selectedConflictTypes}
        employerRole={state.employerRole}
        errors={formErrors}
        onNameChange={(value) => onFieldChange('name', value)}
        onPackageTypeChange={(value) => onFieldChange('documentPackageType', value)}
        onLanguageChange={(value) => onFieldChange('language', value)}
        onFilesChange={(files) => onFieldChange('files', files)}
        onSelectedConflictTypesChange={(value) => onFieldChange('selectedConflictTypes', value)}
        onEmployerRoleChange={(value) => onFieldChange('employerRole', value)}
        rfpRegion={state.rfpRegion}
        onRfpRegionChange={(value) => onFieldChange('rfpRegion', value)}
        // Bid Review (OFFER) props
        bidReviewBids={state.bidReviewBids}
        bidReviewServices={state.bidReviewServices}
        bidReviewAutoFillLevelingSheet={state.bidReviewAutoFillLevelingSheet}
        bidReviewLevelingSheetFiles={state.bidReviewLevelingSheetFiles}
        tenderPackageMode={state.tenderPackageMode}
        existingTenderPackageId={state.existingTenderPackageId}
        tenderFiles={state.tenderFiles}
        onBidReviewBidsChange={(value) => onFieldChange('bidReviewBids', value)}
        onBidReviewServicesChange={(value) => onFieldChange('bidReviewServices', value)}
        onBidReviewAutoFillLevelingSheetChange={(value) =>
          onFieldChange('bidReviewAutoFillLevelingSheet', value)
        }
        onBidReviewLevelingSheetFilesChange={(value) =>
          onFieldChange('bidReviewLevelingSheetFiles', value)
        }
        onTenderPackageModeChange={(value) => onFieldChange('tenderPackageMode', value)}
        onExistingTenderPackageIdChange={(value) => onFieldChange('existingTenderPackageId', value)}
        onTenderFilesChange={(value) => onFieldChange('tenderFiles', value)}
        // Version Compare props
        versionComparePrimaryMode={state.versionComparePrimaryMode}
        versionCompareSecondaryMode={state.versionCompareSecondaryMode}
        versionComparePrimaryPackageId={state.versionComparePrimaryPackageId}
        versionCompareSecondaryPackageId={state.versionCompareSecondaryPackageId}
        versionComparePrimaryFiles={state.versionComparePrimaryFiles}
        versionCompareSecondaryFiles={state.versionCompareSecondaryFiles}
        versionCompareCommentsMode={state.versionCompareCommentsMode}
        versionCompareCommentRegisterFiles={state.versionCompareCommentRegisterFiles}
        onVersionComparePrimaryModeChange={(value) =>
          onFieldChange('versionComparePrimaryMode', value)
        }
        onVersionCompareSecondaryModeChange={(value) =>
          onFieldChange('versionCompareSecondaryMode', value)
        }
        onVersionComparePrimaryPackageIdChange={(value) =>
          onFieldChange('versionComparePrimaryPackageId', value)
        }
        onVersionCompareSecondaryPackageIdChange={(value) =>
          onFieldChange('versionCompareSecondaryPackageId', value)
        }
        onVersionComparePrimaryFilesChange={(value) =>
          onFieldChange('versionComparePrimaryFiles', value)
        }
        onVersionCompareSecondaryFilesChange={(value) =>
          onFieldChange('versionCompareSecondaryFiles', value)
        }
        onVersionCompareCommentsModeChange={(value) =>
          onFieldChange('versionCompareCommentsMode', value)
        }
        onVersionCompareCommentRegisterFilesChange={(value) =>
          onFieldChange('versionCompareCommentRegisterFiles', value)
        }
        // Site Due Diligence props
        siteDueDiligenceFacilityType={state.siteDueDiligenceFacilityType}
        // templateMode={state.templateMode}
        // customTemplateFile={state.customTemplateFile}
        // selectedCustomer={state.selectedCustomer}
        locationLat={state.locationLat}
        locationLon={state.locationLon}
        documentFiles={state.documentFiles}
        // gisFiles={state.gisFiles}
        // urls={state.urls}
        // allowLLMForDocuments={state.allowLLMForDocuments}
        onSiteDueDiligenceFacilityTypeChange={(value) =>
          onFieldChange('siteDueDiligenceFacilityType', value)
        }
        // onTemplateModeChange={(value) => onFieldChange('templateMode', value)}
        // onCustomTemplateFileChange={(value) => onFieldChange('customTemplateFile', value)}
        // onSelectedCustomerChange={(value) => onFieldChange('selectedCustomer', value)}
        onLocationLatChange={(value) => onFieldChange('locationLat', value)}
        onLocationLonChange={(value) => onFieldChange('locationLon', value)}
        onDocumentFilesChange={(value) => onFieldChange('documentFiles', value)}
        // onGisFilesChange={(value) => onFieldChange('gisFiles', value)}
        // onUrlsChange={(value) => onFieldChange('urls', value)}
        // onAllowLLMForDocumentsChange={(value) => onFieldChange('allowLLMForDocuments', value)}
      />
    );
  }

  if (serviceType === ServiceEntityType.BUILDING_PACKAGE) {
    return (
      <BuildingPackageForm
        name={state.name}
        packageType={state.buildingPackageType}
        inputType={state.inputType}
        files={state.files}
        errors={formErrors}
        onNameChange={(value) => onFieldChange('name', value)}
        onPackageTypeChange={(value) => onFieldChange('buildingPackageType', value)}
        onInputTypeChange={(value) => onFieldChange('inputType', value)}
        onFilesChange={(files) => onFieldChange('files', files)}
      />
    );
  }

  if (serviceType === ServiceEntityType.LAYOUT_OPTIMIZATION) {
    return (
      <LayoutOptimizationForm
        name={state.name}
        layoutType={state.layoutOptimizationType}
        language={state.language}
        comments={state.comments}
        floorHeight={state.floorHeight}
        files={state.files}
        errors={formErrors}
        onNameChange={(value) => onFieldChange('name', value)}
        onLayoutTypeChange={(value) => onFieldChange('layoutOptimizationType', value)}
        onLanguageChange={(value) => onFieldChange('language', value)}
        onCommentsChange={(value) => onFieldChange('comments', value)}
        onFloorHeightChange={(value) => onFieldChange('floorHeight', value)}
        onFilesChange={(files) => onFieldChange('files', files)}
      />
    );
  }

  return null;
};
